
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapGetters } from 'vuex'
import { EAuthMethod } from '@/helpers/enums'
import { validateEnum } from '@/helpers/index'

export default {
  name: 'LoginForm',
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    selectedMethod: {
      validator(value) {
        return validateEnum(value, EAuthMethod)
      },
      default: EAuthMethod.EMAIL
    },
    loginErrorState: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      password: '',
      showPassword: false
    }
  },
  computed: {
    ...mapGetters({
      emailRegisterToLogin: 'emailRegisterToLogin',
      mobileCodeGetter: 'mobile/mobileCode',
      mobileCallingCodeGetter: 'mobile/mobileCallingCode',
      mobileNumberGetter: 'mobile/mobileNumber'
    }),
    formId() {
      return this.$getElementId(this.$route.path, 'form', { stepName: this.selectedMethod })
    },

    formData: {
      get() {
        return {
          email: this.emailRegisterToLogin || '',
          password: this.password,
          mobilePhone: {
            countryCode: this.mobileCodeGetter || this.$store.getters.defaultCountry,
            number: this.mobileNumberGetter,
            code: this.mobileCallingCodeGetter
          }
        }
      },
      set(val) {
        if ('email' in val) {
          this.$store.commit('EMAIL_REGISTER_TO_LOGIN', val.email)
        }

        if ('password' in val) {
          this.password = val.password
        }

        if ('mobilePhone' in val) {
          this.$store.commit('mobile/MOBILE_CODE', val.mobilePhone.countryCode)
          this.$store.commit('mobile/MOBILE_CALLING_CODE', val.mobilePhone.code)
          this.$store.commit('mobile/MOBILE_NUMBER', val.mobilePhone.number)
        }
      }
    },

    loginInputs() {
      const inputs = []

      const isSpecialCharsAllowed = this.$useConfig(this.$EClientConfig.EMAIL_SPECIAL_CHARACTERS)

      if (this.isEmailSelected) {
        inputs.push(
          {
            id: 'email',
            label: this.$t('talent.login.label.email'),
            rules: `required|email${!isSpecialCharsAllowed ? '|specialChars' : ''}`,
            type: 'email',
            inputMode: 'email',
            autocapitalize: 'off',
            prependInnerIcon: '$vuetify.icons.mdiEmailOutline',
            appendIcon: ''
          },
          {
            id: 'password',
            label: this.$t('talent.login.label.password'),
            rules: 'required',
            type: this.showPassword ? 'text' : 'password',
            inputMode: 'password',
            autocapitalize: 'off',
            prependInnerIcon: '$vuetify.icons.mdiLockOutline',
            appendIcon: this.showPassword ? '$vuetify.icons.mdiEye' : '$vuetify.icons.mdiEyeOff'
          }
        )
      } else if (this.isOneTimeLinkSelected) {
        inputs.push({
          id: 'email',
          label: this.$t('talent.login.label.email'),
          rules: 'required|email',
          type: 'email',
          inputmode: 'email',
          autocapitalize: 'off',
          prependInnerIcon: '$vuetify.icons.mdiEmailOutline',
          appendIcon: ''
        })
      } else if (this.isMobileSelected) {
        inputs.push({
          id: 'mobilePhone',
          label: '',
          rules: 'validPhone',
          name: 'mobile phone',
          debounce: 200,
          prependInnerIcon: '$vuetify.icons.mdiCellphoneText'
        })
      }

      return inputs
    },

    isEmailSelected() {
      return this.selectedMethod === EAuthMethod.EMAIL
    },
    isOneTimeLinkSelected() {
      return this.selectedMethod === EAuthMethod.ONE_TIME_LINK
    },
    isMobileSelected() {
      return this.selectedMethod === EAuthMethod.MOBILE
    },
    card() {
      if (this.isEmailSelected) {
        return {
          title: this.$t('talent.login.with.method', {
            method: this.$t(`talent.login.method.${this.selectedMethod}`)
          }),
          subtitle: this.$t('talent.login.enter.your.credentials'),
          submitBtnText: this.$t('talent.login.button.submit')
        }
      } else if (this.isOneTimeLinkSelected) {
        return {
          title: this.$t('talent.login.with.method', {
            method: this.$t(`talent.login.method.${this.selectedMethod}`)
          }),
          subtitle: this.$t('talent.login.send.one.time.link.to.account'),
          submitBtnText: this.$t('talent.login.button.send.one.time.link')
        }
      } else if (this.isMobileSelected) {
        return {
          title: this.$t('talent.login.with.method', {
            method: this.$t(`talent.login.method.${this.selectedMethod}`)
          }),
          subtitle: this.$t('talent.login.text.mobile.welcome'),
          submitBtnText: this.$t('talent.register.button.mobile.submit')
        }
      } else {
        return {
          title: '',
          subtitle: '',
          submitBtnText: ''
        }
      }
    }
  },

  methods: {
    async handleSubmitForm() {
      const isValid = await this.$refs.loginObserver.validate()

      if (isValid) {
        if (this.isMobileSelected) {
          this.$emit('submit:mobile', {
            mobileCode: this.formData.mobilePhone.code,
            mobileNumber: this.formData.mobilePhone.number,
            mobileCountryCode: this.formData.mobilePhone.countryCode
          })
        } else if (this.isEmailSelected) {
          this.$emit('submit:email', {
            email: this.formData.email,
            password: this.formData.password
          })
        } else if (this.isOneTimeLinkSelected) {
          this.$emit('submit:oneTimeLink', {
            email: this.formData.email
          })
        }
      }
    },

    togglePassword() {
      this.showPassword = !this.showPassword
    },
    updateFormData(key, value) {
      const updatedFormData = { ...this.formData }
      updatedFormData[key] = value
      this.formData = updatedFormData
      this.$emit('disable:button', false)
    }
  }
}
